import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { TrendUp } from "@phosphor-icons/react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-zinc-950 text-zinc-100">
      {/* left brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden border-r border-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHN1YnRsZSUyMG9yZ2FuaWMlMjB0ZXh0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODU4MTI0NDV8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/70 via-zinc-950/40 to-emerald-950/40" />
        <div className="relative z-10 flex flex-col justify-between p-14">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <TrendUp size={22} weight="bold" />
            </div>
            <div>
              <div className="text-xl font-display font-semibold">Sirbansal</div>
              <div className="text-xs text-zinc-400 tracking-widest uppercase">Private Wealth OS</div>
            </div>
          </div>

          <div className="max-w-lg">
            <h1 className="font-display text-5xl font-light leading-tight tracking-tight">
              One console for your <span className="text-emerald-400">portfolio</span>, banks, and AI advisor.
            </h1>
            <p className="mt-6 text-zinc-400 text-base leading-relaxed">
              Track every rupee across your investments and bank accounts, train the AI on your investing style,
              and get personalised buy / sell / hold guidance — all in one private dashboard.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 text-sm">
              {["Live NSE/BSE quotes", "Multi-bank net worth", "AI recommendations"].map((t) => (
                <div key={t} className="border border-zinc-800/70 rounded-lg p-3 bg-zinc-900/40 backdrop-blur">
                  <div className="text-emerald-400 font-mono text-xs">●</div>
                  <div className="mt-2 text-zinc-300">{t}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-zinc-500 font-mono">© Sirbansal · Private access only</div>
        </div>
      </div>

      {/* right form panel */}
      <div className="lg:col-span-2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-emerald-600 flex items-center justify-center">
              <TrendUp size={20} weight="bold" />
            </div>
            <span className="font-display text-lg font-semibold">Sirbansal</span>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-bold">Sign in</div>
          <h2 className="mt-2 font-display text-3xl">Welcome back</h2>
          <p className="mt-2 text-sm text-zinc-400">Access your private wealth dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-emerald-500" />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-emerald-500" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md h-11">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-zinc-400 text-center">
            New here?{" "}
            <Link to="/signup" data-testid="go-to-signup-link" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-4">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
